import Link from "next/link";
import type { ReactNode } from "react";

import { DotGridBackdrop } from "../../dot-grid-backdrop";
import { DARK_PANEL_BG, H } from "../hero-tokens";

/**
 * The closing CTA panel — the one dark band on the page: ink panel, dot grid
 * fading in from a top spotlight, white primary button. Shared by the desktop
 * ({@link HeroCta}) and mobile ({@link HeroMobileClosingCta}) sections;
 * `compact` is the phone layout.
 */
export function HeroCtaPanel(props: {
  readonly headline: ReactNode;
  readonly sub: string;
  readonly buttonLabel: string;
  readonly compact: boolean;
}): ReactNode {
  const { compact } = props;
  return (
    <div
      style={{
        position: "relative",
        borderRadius: 24,
        overflow: "hidden",
        background: DARK_PANEL_BG,
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 40px 90px -50px rgba(11,15,25,0.6)",
        padding: compact ? "52px 22px 54px" : "84px 40px 88px",
        textAlign: "center",
        color: "#fff",
      }}
    >
      <DotGridBackdrop tone="dark" focus="top" />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          background:
            "radial-gradient(60% 55% at 50% -8%, rgba(255,255,255,0.16), transparent 70%)",
        }}
      />
      <div style={{ position: "relative", zIndex: 2 }}>
        <h2
          style={{
            fontFamily: H.display,
            fontWeight: 600,
            fontSize: compact ? 32 : 54,
            lineHeight: 1.07,
            letterSpacing: "-0.035em",
            color: "#fff",
            margin: "0 auto",
            maxWidth: 740,
          }}
        >
          {props.headline}
        </h2>
        <p
          style={{
            fontSize: compact ? 15 : 18,
            lineHeight: 1.55,
            color: "rgba(255,255,255,0.62)",
            margin: compact ? "14px auto 0" : "18px auto 0",
            maxWidth: 520,
          }}
        >
          {props.sub}
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 32,
          }}
        >
          <Link
            href="/demo"
            className="mkhero-accentbtn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              background: "#fff",
              color: H.ink,
              padding: compact ? "15px 30px" : "16px 32px",
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            {props.buttonLabel} <span style={{ fontSize: 13 }}>{"↗"}</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Emphasised word inside the dark headline: upright, same white as the rest. */
export function heroCtaEmphasis(chunks: ReactNode): ReactNode {
  return <em style={{ fontStyle: "normal" }}>{chunks}</em>;
}
